import React, { useMemo, useState, useCallback, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, gql } from "@apollo/client";
import { Helmet } from "react-helmet";
import { useLang } from "../../context/lang-context";
import "./RestaurantMenuPage.css";
import ProductDetailModal from "./ProductDetailModal";

const BASE_URL = "https://fooapp.mx";

// wa.me links pass the message as a URL query param — encodeURIComponent
// triples the length of characters like *, _, |, #, á, ñ, so orders with
// several items/modifiers can blow past what WhatsApp's link handler
// reliably opens (it truncates/fails silently well under the raw message
// limit). Past this threshold we fall back to a short greeting-only link
// and ask the user to paste the full order instead.
const WHATSAPP_URL_SAFE_LENGTH = 2000;

const GET_RESTAURANT_BY_URL = gql`
  query GetRestaurantByUrl($url_name: String!) {
    getRestaurantByUrl(url_name: $url_name) {
      id
      company_name
      about
      short_desc
      logo
      cover_img
      phone_main
      webpage
      fb_page
      instagram
      branches {
        id
        name
        address
        phone
        whatsapp_phone
        city
        location {
          latitude
          longitude
        }
        hours {
          weekday
          start_hour
          end_hour
          opens
        }
      }
      products {
        id
        title
        description
        price
        has_size
        snoozed
        status_web
        images {
          id
          url
          type
        }
        category {
          id
          name
          name_en
        }
        sizes {
          id
          name
          name_en
          price
        }
        has_option
        ingredients {
          id
          name
          name_en
        }
        options {
          id
          id_ingredient
          price_mod
          ingredient {
            id
            name
            name_en
          }
        }
        option_groups {
          id
          name
          name_en
          min_selections
          max_selections
          sort_order
          options {
            id
            id_ingredient
            price_mod
            ingredient {
              id
              name
              name_en
            }
          }
        }
      }
    }
  }
`;

interface Hour {
  weekday: number;
  start_hour: string;
  end_hour: string;
  opens: boolean;
}

interface Branch {
  id: string;
  name: string;
  address: string;
  phone: string;
  whatsapp_phone: string | null;
  city: string;
  location: { latitude: number; longitude: number } | null;
  hours: Hour[];
}

interface ExpressCartItem {
  productId: string;
  title: string;
  price: number;
  sizeId: string | null;
  sizeName: string | null;
  quantity: number;
  deselectedIngredientIds: string[];
  selectedOptionIds: string[];
  ingredientsTxt: string;
  comments: string;
}

interface ProductImage {
  id: string;
  url: string;
  type: string;
}

interface Category {
  id: string;
  name: string;
  name_en: string;
}

interface Size {
  id: string;
  name: string;
  name_en: string;
  price: number;
}

interface Ingredient {
  id: string;
  name: string;
  name_en: string;
}

interface OptionIngredient {
  id: string;
  name: string;
  name_en: string;
}

interface ProductOption {
  id: string;
  id_ingredient: string;
  price_mod: number;
  ingredient: OptionIngredient | null;
}

interface OptionGroup {
  id: string;
  name: string;
  name_en: string | null;
  min_selections: number;
  max_selections: number;
  sort_order: number;
  options: ProductOption[];
}

interface Product {
  id: string;
  title: string;
  description: string;
  price: number;
  has_size: boolean;
  has_option: number;
  snoozed: boolean;
  status_web: boolean;
  images: ProductImage[];
  category: Category | null;
  sizes: Size[];
  ingredients: Ingredient[];
  options: ProductOption[];
  option_groups: OptionGroup[];
}

interface Restaurant {
  id: string;
  company_name: string;
  about: string | null;
  short_desc: string | null;
  logo: string | null;
  cover_img: string | null;
  phone_main: string | null;
  webpage: string | null;
  fb_page: string | null;
  instagram: string | null;
  branches: Branch[];
  products: Product[];
}

function resolveImageUrl(path: string | null | undefined): string | null {
  if (!path) return null;
  if (path.startsWith("http")) return path;
  return `${BASE_URL}${path}`;
}

// Backend weekdays: 1=Mon, 2=Tue, ..., 7=Sun
const WEEKDAYS_ES: Record<number, string> = { 1: "Lunes", 2: "Martes", 3: "Miércoles", 4: "Jueves", 5: "Viernes", 6: "Sábado", 7: "Domingo" };
const WEEKDAYS_EN: Record<number, string> = { 1: "Monday", 2: "Tuesday", 3: "Wednesday", 4: "Thursday", 5: "Friday", 6: "Saturday", 7: "Sunday" };

// Convert JS getDay() (0=Sun) to backend weekday (7=Sun)
function jsToBackendWeekday(): number {
  const jsDay = new Date().getDay();
  return jsDay === 0 ? 7 : jsDay;
}

function to12h(time: string): string {
  const [h, m] = time.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return m === 0 ? `${hour12} ${period}` : `${hour12}:${m.toString().padStart(2, "0")} ${period}`;
}

function getTodayHours(hours: Hour[], lang: string): string {
  const today = jsToBackendWeekday();
  const todayHours = hours.filter((h) => h.weekday === today && h.opens);
  if (todayHours.length === 0) {
    return lang === "es" ? "Cerrado hoy" : "Closed today";
  }
  return todayHours
    .map((h) => `${to12h(h.start_hour)} - ${to12h(h.end_hour)}`)
    .join(", ");
}

function isOpenNow(hours: Hour[]): boolean {
  const now = new Date();
  const today = jsToBackendWeekday();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  return hours.some((h) => {
    if (h.weekday !== today || !h.opens) return false;
    const [startH, startM] = h.start_hour.split(":").map(Number);
    const [endH, endM] = h.end_hour.split(":").map(Number);
    const start = startH * 60 + startM;
    const end = endH * 60 + endM;
    if (end < start) {
      // Overnight: open if after start OR before end
      return currentMinutes >= start || currentMinutes <= end;
    }
    return currentMinutes >= start && currentMinutes <= end;
  });
}

function formatPrice(price: number): string {
  return `$${price.toFixed(2)}`;
}

const CLOSING_SOON_THRESHOLD_MIN = 60;

function formatMinutesUntil(minutesUntil: number, lang: string): string {
  if (minutesUntil <= 1) {
    return lang === "es" ? "en un momento" : "in a moment";
  }
  if (minutesUntil < 60) {
    return lang === "es" ? `en ${minutesUntil} min` : `in ${minutesUntil} min`;
  }
  const hours = Math.round(minutesUntil / 60);
  if (lang === "es") {
    return hours === 1 ? "en 1 hora" : `en ${hours} horas`;
  }
  return hours === 1 ? "in 1 hour" : `in ${hours} hours`;
}

// Returns a short helper string like "Abre en 4 min", "Abre a las 12:00 PM",
// "Abre el Lunes a las 12:00 PM", or "Cierra en 20 min" — or null when there's
// nothing useful to say (open with plenty of time left, or no hours configured).
function getOpenStatusHelpText(hours: Hour[], isOpen: boolean, lang: string): string | null {
  const now = new Date();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const today = jsToBackendWeekday();

  if (isOpen) {
    // Find the interval covering "now" and report time left until it ends.
    const activeInterval = hours.find((h) => {
      if (h.weekday !== today || !h.opens) return false;
      const [startH, startM] = h.start_hour.split(":").map(Number);
      const [endH, endM] = h.end_hour.split(":").map(Number);
      const start = startH * 60 + startM;
      const end = endH * 60 + endM;
      if (end < start) {
        return currentMinutes >= start || currentMinutes <= end;
      }
      return currentMinutes >= start && currentMinutes <= end;
    });
    if (!activeInterval) return null;
    const [endH, endM] = activeInterval.end_hour.split(":").map(Number);
    let end = endH * 60 + endM;
    if (end < currentMinutes) end += 24 * 60; // overnight interval, ends "tomorrow"
    const minutesUntilClose = end - currentMinutes;
    if (minutesUntilClose > CLOSING_SOON_THRESHOLD_MIN) return null;
    return lang === "es"
      ? `Cierra ${formatMinutesUntil(minutesUntilClose, lang)}`
      : `Closes ${formatMinutesUntil(minutesUntilClose, lang)}`;
  }

  // Closed: scan forward (today .. today+6) for the next interval that opens.
  for (let offset = 0; offset <= 6; offset++) {
    const weekday = ((today - 1 + offset) % 7) + 1;
    const dayIntervals = hours
      .filter((h) => h.weekday === weekday && h.opens)
      .sort((a, b) => a.start_hour.localeCompare(b.start_hour));
    for (const interval of dayIntervals) {
      const [startH, startM] = interval.start_hour.split(":").map(Number);
      const start = startH * 60 + startM;
      if (offset === 0 && start <= currentMinutes) continue; // already passed today
      if (offset === 0) {
        const minutesUntilOpen = start - currentMinutes;
        return lang === "es"
          ? `Abre ${formatMinutesUntil(minutesUntilOpen, lang)}`
          : `Opens ${formatMinutesUntil(minutesUntilOpen, lang)}`;
      }
      if (offset === 1) {
        return lang === "es"
          ? `Abre mañana a las ${to12h(interval.start_hour)}`
          : `Opens tomorrow at ${to12h(interval.start_hour)}`;
      }
      const dayName = lang === "es" ? WEEKDAYS_ES[weekday] : WEEKDAYS_EN[weekday];
      return lang === "es"
        ? `Abre el ${dayName} a las ${to12h(interval.start_hour)}`
        : `Opens ${dayName} at ${to12h(interval.start_hour)}`;
    }
  }
  return null;
}

const RestaurantMenuPage: React.FC = () => {
  const { url_name } = useParams<{ url_name: string }>();
  const { lang, setLang, t } = useLang();
  const [lightboxImage, setLightboxImage] = useState<{ url: string; title: string } | null>(null);
  const [expressCart, setExpressCart] = useState<ExpressCartItem[]>([]);
  const [cartExpanded, setCartExpanded] = useState(false);
  const [detailProduct, setDetailProduct] = useState<Product | null>(null);
  const [pastHeader, setPastHeader] = useState(false);
  const coverRef = useRef<HTMLElement | null>(null);

  const scrollToTop = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const { data, loading, error } = useQuery(GET_RESTAURANT_BY_URL, {
    variables: { url_name },
    skip: !url_name,
  });

  const restaurant: Restaurant | null = data?.getRestaurantByUrl;
  const mainBranch = restaurant?.branches?.[0];

  useEffect(() => {
    if (!cartExpanded) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [cartExpanded]);

  useEffect(() => {
    const coverEl = coverRef.current;
    if (!coverEl) return;
    const observer = new IntersectionObserver(
      ([entry]) => setPastHeader(!entry.isIntersecting),
      { rootMargin: "-56px 0px 0px 0px", threshold: 0 }
    );
    observer.observe(coverEl);
    return () => observer.disconnect();
  }, [restaurant]);

  const openLightbox = useCallback((url: string, title: string) => {
    setLightboxImage({ url, title });
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxImage(null);
  }, []);

  const addToCart = useCallback((item: {
    productId: string;
    title: string;
    price: number;
    sizeId: string | null;
    sizeName: string | null;
    quantity: number;
    deselectedIngredientIds: string[];
    selectedOptionIds: string[];
    ingredientsTxt: string;
    comments: string;
  }) => {
    setExpressCart((prev) => {
      return [
        ...prev,
        {
          productId: item.productId,
          title: item.title,
          price: item.price,
          sizeId: item.sizeId,
          sizeName: item.sizeName,
          quantity: item.quantity,
          deselectedIngredientIds: item.deselectedIngredientIds,
          selectedOptionIds: item.selectedOptionIds,
          ingredientsTxt: item.ingredientsTxt,
          comments: item.comments,
        },
      ];
    });
    setDetailProduct(null);
  }, []);

  const removeFromCart = useCallback((productId: string, sizeId: string | null) => {
    setExpressCart((prev) => {
      const existing = prev.find(
        (item) => item.productId === productId && item.sizeId === sizeId
      );
      if (existing && existing.quantity > 1) {
        return prev.map((item) =>
          item.productId === productId && item.sizeId === sizeId
            ? { ...item, quantity: item.quantity - 1 }
            : item
        );
      }
      return prev.filter(
        (item) => !(item.productId === productId && item.sizeId === sizeId)
      );
    });
  }, []);

  const cartItemCount = useMemo(() => expressCart.reduce((sum, item) => sum + item.quantity, 0), [expressCart]);
  const cartTotal = useMemo(() => expressCart.reduce((sum, item) => sum + item.price * item.quantity, 0), [expressCart]);

  const handleAddClick = useCallback((product: Product) => {
    setDetailProduct(product);
  }, []);

  const generateWhatsAppMessage = useCallback(() => {
    if (!restaurant) return "";
    let msg = `${t("Hola! Quisiera hacer un pedido", "Hi! I'd like to place an order")}:\n\n`;
    msg += `*${restaurant.company_name}*\n\n`;
    expressCart.forEach((item) => {
      const sizeSuffix = item.sizeName ? ` (${item.sizeName})` : "";
      const idTag = item.sizeId
        ? `(#${item.productId}-S${item.sizeId})`
        : `(#${item.productId})`;
      msg += `${item.quantity}x ${item.title}${sizeSuffix} ${idTag} - ${formatPrice(item.price * item.quantity)}\n`;
      if (item.ingredientsTxt) {
        msg += `  _${item.ingredientsTxt}_\n`;
      }
      if (item.comments) {
        msg += `  ${t("Nota", "Note")}: ${item.comments}\n`;
      }
    });
    msg += `\n*Total: ${formatPrice(cartTotal)}*`;
    return msg;
  }, [restaurant, expressCart, cartTotal, t]);

  const isMessageTooLongForLink = useMemo(() => {
    return encodeURIComponent(generateWhatsAppMessage()).length > WHATSAPP_URL_SAFE_LENGTH;
  }, [generateWhatsAppMessage]);

  const getWhatsAppUrl = useCallback(() => {
    if (!mainBranch) return "#";
    const phone = mainBranch.whatsapp_phone || mainBranch.phone;
    if (!phone) return "#";
    const cleanPhone = phone.replace(/\D/g, "");
    const fullMessage = generateWhatsAppMessage();
    const message = encodeURIComponent(
      isMessageTooLongForLink
        ? `${t("Hola! Quisiera hacer un pedido", "Hi! I'd like to place an order")}:`
        : fullMessage
    );
    return `https://wa.me/${cleanPhone}?text=${message}`;
  }, [mainBranch, generateWhatsAppMessage, isMessageTooLongForLink, t]);

  const groupedProducts = useMemo(() => {
    if (!restaurant?.products) return {};
    const groups: Record<string, Product[]> = {};
    restaurant.products
      .filter((p) => p.status_web && !p.snoozed)
      .forEach((product) => {
        const catName = product.category
          ? lang === "es"
            ? product.category.name
            : product.category.name_en || product.category.name
          : lang === "es"
          ? "Otros"
          : "Other";
        if (!groups[catName]) groups[catName] = [];
        groups[catName].push(product);
      });
    return groups;
  }, [restaurant, lang]);

  if (loading) {
    return (
      <div className="rm-loading">
        <div className="rm-spinner" />
      </div>
    );
  }

  if (error || !restaurant) {
    return (
      <div className="rm-not-found">
        <img src="/img/logo_svg.svg" alt="Foo" className="rm-not-found-logo" />
        <h2>{t("Restaurante no encontrado", "Restaurant not found")}</h2>
        <p>{t("El enlace puede ser incorrecto o el restaurante no está disponible.", "The link may be incorrect or the restaurant is unavailable.")}</p>
        <Link to="/" className="rm-btn-home">
          {t("Ir a Foo", "Go to Foo")}
        </Link>
      </div>
    );
  }

  const coverUrl = resolveImageUrl(restaurant.cover_img);
  const logoUrl = resolveImageUrl(restaurant.logo);
  const open = mainBranch ? isOpenNow(mainBranch.hours) : false;
  const statusHelpText = mainBranch ? getOpenStatusHelpText(mainBranch.hours, open, lang) : null;

  const handleShare = async () => {
    const shareData = {
      title: restaurant.company_name,
      text: restaurant.short_desc || restaurant.company_name,
      url: window.location.href,
    };
    if (navigator.share) {
      try { await navigator.share(shareData); } catch {}
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert(t("Enlace copiado", "Link copied"));
    }
  };

  return (
    <>
      <Helmet>
        <title>{`${restaurant.company_name} | Foo`}</title>
        <meta name="theme-color" content="#ffbb28" />
        <meta
          name="description"
          content={restaurant.short_desc || restaurant.about || restaurant.company_name}
        />
        <html lang={lang} />
      </Helmet>

      {/* Nav */}
      <nav className={`rm-nav ${pastHeader ? "rm-nav-branded" : ""}`}>
        <div className="container d-flex align-items-center justify-content-between">
          <a href="/" onClick={scrollToTop} className="rm-nav-brand">
            <span className="rm-nav-logo-stack">
              <img src="/img/logo_svg.svg" alt="Foo" className="rm-nav-logo rm-nav-logo-foo" />
              {logoUrl && (
                <img
                  src={logoUrl}
                  alt={restaurant.company_name}
                  className="rm-nav-logo rm-nav-logo-restaurant"
                />
              )}
            </span>
            {pastHeader && (
              <span className="rm-nav-restaurant-info">
                <span className="rm-nav-restaurant-name">{restaurant.company_name}</span>
                {mainBranch && (
                  <span className={`rm-nav-badge ${open ? "rm-badge-open" : "rm-badge-closed"}`}>
                    {open ? t("Abierto", "Open") : t("Cerrado", "Closed")}
                  </span>
                )}
                {statusHelpText && (
                  <span className="rm-nav-status-help">{statusHelpText}</span>
                )}
              </span>
            )}
          </a>
          <div className="rm-nav-actions">
            <button
              className="btn btn-outline-dark btn-sm rm-share-btn"
              onClick={handleShare}
              aria-label={t("Compartir", "Share")}
            >
              <i className="fas fa-share-alt"></i>
            </button>
            <button
              className="btn btn-outline-dark btn-sm rm-lang-btn"
              onClick={() => setLang(lang === "es" ? "en" : "es")}
            >
              {lang === "es" ? "EN" : "ES"}
            </button>
          </div>
        </div>
      </nav>

      {/* Cover */}
      <header
        ref={coverRef}
        className="rm-cover"
        style={coverUrl ? { backgroundImage: `url(${coverUrl})` } : {}}
      >
        <div className="rm-cover-overlay" />
        <div className="container rm-cover-content">
          {logoUrl && <img src={logoUrl} alt={restaurant.company_name} className="rm-logo" />}
          <h1 className="rm-name">{restaurant.company_name}</h1>
          {restaurant.short_desc && <p className="rm-desc">{restaurant.short_desc}</p>}
          {mainBranch && (
            <div className="rm-status-row">
              <span className={`rm-badge ${open ? "rm-badge-open" : "rm-badge-closed"}`}>
                {open ? t("Abierto", "Open") : t("Cerrado", "Closed")}
              </span>
              {statusHelpText && (
                <span className="rm-status-help">{statusHelpText}</span>
              )}
              <span className="rm-hours-today">
                {getTodayHours(mainBranch.hours, lang)}
              </span>
            </div>
          )}
        </div>
      </header>

      {/* Mobile info section */}
      {mainBranch && (
        <div className="rm-mobile-info d-lg-none">
          <div className="container">
            <div className="rm-mobile-actions">
              {mainBranch.phone && (
                <a href={`tel:${mainBranch.phone}`} className="rm-cta-call">
                  <i className="fas fa-phone"></i> {t("Llamar", "Call")}
                </a>
              )}
              <button className="rm-cta-share" onClick={handleShare}>
                <i className="fas fa-share-alt"></i> {t("Compartir", "Share")}
              </button>
            </div>
            <div className="rm-mobile-details">
              {mainBranch.address && (
                <p className="rm-mobile-address">
                  <i className="fa fa-map-marker-alt"></i> {mainBranch.address}
                  {mainBranch.city && `, ${mainBranch.city}`}
                </p>
              )}
              {mainBranch.hours.length > 0 && (
                <p className="rm-mobile-hours">
                  <i className="far fa-clock"></i>{" "}
                  {open ? t("Abierto", "Open") : t("Cerrado", "Closed")}
                  {" · "}
                  {getTodayHours(mainBranch.hours, lang)}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="container rm-body">
        <div className="row">
          {/* Menu */}
          <div className="col-lg-8">
            <h2 className="rm-section-title">
              {t("Menú", "Menu")}
            </h2>

            {Object.keys(groupedProducts).length === 0 && (
              <p className="rm-empty">{t("No hay productos disponibles.", "No products available.")}</p>
            )}

            {Object.entries(groupedProducts).map(([category, products]) => (
              <div key={category} className="rm-category">
                <h3 className="rm-category-title">{category}</h3>
                <div className="rm-products">
                  {products.map((product) => (
                    <ProductCard key={product.id} product={product} lang={lang} onImageClick={openLightbox} onAdd={handleAddClick} />
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Sidebar */}
          <div className="col-lg-4">
            <div className="rm-sidebar">
              {/* Info */}
              {restaurant.about && (
                <div className="rm-info-card">
                  <h4>{t("Acerca de", "About")}</h4>
                  <p>{restaurant.about}</p>
                </div>
              )}

              {/* Branch info */}
              {mainBranch && (
                <div className="rm-info-card">
                  <h4>{t("Ubicación", "Location")}</h4>
                  {mainBranch.location && process.env.REACT_APP_MAPS_KEY && (
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${mainBranch.location.latitude},${mainBranch.location.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="rm-map-link"
                    >
                      <img
                        src={`https://maps.googleapis.com/maps/api/staticmap?center=${mainBranch.location.latitude},${mainBranch.location.longitude}&zoom=15&size=600x200&scale=2&markers=color:0xFFBB28|${mainBranch.location.latitude},${mainBranch.location.longitude}&key=${process.env.REACT_APP_MAPS_KEY}`}
                        alt={t("Mapa", "Map")}
                        className="rm-map-img"
                      />
                    </a>
                  )}
                  <p className="rm-address">
                    <i className="fa fa-map-marker-alt"></i> {mainBranch.address}
                    {mainBranch.city && `, ${mainBranch.city}`}
                  </p>
                  {mainBranch.phone && (
                    <p className="rm-phone">
                      <i className="fa fa-phone"></i>{" "}
                      <a href={`tel:${mainBranch.phone}`}>{mainBranch.phone}</a>
                    </p>
                  )}
                </div>
              )}

              {/* Hours */}
              {mainBranch && mainBranch.hours.length > 0 && (
                <div className="rm-info-card">
                  <h4>{t("Horario", "Hours")}</h4>
                  <HoursTable hours={mainBranch.hours} lang={lang} />
                </div>
              )}

              {/* Social links */}
              {(restaurant.fb_page || restaurant.instagram || restaurant.webpage) && (
                <div className="rm-info-card rm-social">
                  {restaurant.webpage && (
                    <a href={restaurant.webpage} target="_blank" rel="noopener noreferrer" className="rm-social-link">
                      <i className="fa fa-globe"></i> {t("Sitio web", "Website")}
                    </a>
                  )}
                  {restaurant.fb_page && (
                    <a href={`https://facebook.com/${restaurant.fb_page}`} target="_blank" rel="noopener noreferrer" className="rm-social-link">
                      <i className="fab fa-facebook"></i> Facebook
                    </a>
                  )}
                  {restaurant.instagram && (
                    <a href={`https://instagram.com/${restaurant.instagram}`} target="_blank" rel="noopener noreferrer" className="rm-social-link">
                      <i className="fab fa-instagram"></i> Instagram
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Download Banner */}
      <section className="rm-download">
        <div className="container text-center">
          <p className="rm-download-text">
            {t(
              "Ordena en línea sin costo extra. Descarga nuestra app",
              "Order online at no extra cost. Download our app"
            )}
          </p>
          <div className="rm-download-badges">
            {/* TODO: Replace with real App Store URL */}
            <button type="button" className="rm-store-btn" onClick={() => {}}>
              <img src="/img/appstore-badge.png" alt="App Store" className="rm-store-badge" />
            </button>
            {/* TODO: Replace with real Google Play URL */}
            <button type="button" className="rm-store-btn" onClick={() => {}}>
              <img src="/img/google-play-badge.png" alt="Google Play" className="rm-store-badge" />
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="rm-footer">
        <div className="container text-center">
          <Link to="/">
            <img src="/img/logo_svg.svg" alt="Foo" className="rm-footer-logo" />
          </Link>
          <p className="rm-footer-text">
            {t("Impulsado por", "Powered by")} <strong>Foo</strong>
          </p>
          <a
            href="https://foopos.mx"
            target="_blank"
            rel="noopener noreferrer"
            className="rm-footer-pos-link"
          >
            {t("Registra tu negocio gratis en foopos.mx", "Register your business for free at foopos.mx")}
          </a>
        </div>
      </footer>

      {/* Image Lightbox */}
      {lightboxImage && (
        <div className="rm-lightbox-overlay" onClick={closeLightbox}>
          <button className="rm-lightbox-close" onClick={closeLightbox} aria-label="Close">
            <i className="fas fa-times"></i>
          </button>
          <img
            src={lightboxImage.url}
            alt={lightboxImage.title}
            className="rm-lightbox-img"
            onClick={(e) => e.stopPropagation()}
          />
          <p className="rm-lightbox-caption">{lightboxImage.title}</p>
        </div>
      )}

      {/* Product Detail Modal */}
      {detailProduct && (
        <ProductDetailModal
          product={detailProduct}
          lang={lang}
          t={t}
          onAdd={addToCart}
          onClose={() => setDetailProduct(null)}
        />
      )}

      {/* Backdrop behind the expanded order panel */}
      {cartExpanded && (
        <div className="rm-express-backdrop" onClick={() => setCartExpanded(false)} />
      )}

      {/* Express Cart Bar */}
      {cartItemCount > 0 && (
        <div className={`rm-express-cart ${cartExpanded ? "rm-express-cart-expanded" : ""}`}>
          {cartExpanded && (
            <div className="rm-express-list">
              <div className="rm-express-header">
                <h4>{t("Tu pedido", "Your order")}</h4>
                <button className="rm-express-close" onClick={() => setCartExpanded(false)}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
              {expressCart.map((item) => (
                <div key={`${item.productId}-${item.sizeId}`} className="rm-express-item">
                  <div className="rm-express-item-info">
                    <span className="rm-express-item-name">
                      {item.quantity}x {item.title}
                      {item.sizeName && <small> ({item.sizeName})</small>}
                      {item.ingredientsTxt && <small className="rm-express-item-details"> — {item.ingredientsTxt}</small>}
                      {item.comments && <small className="rm-express-item-details"> | {item.comments}</small>}
                    </span>
                    <span className="rm-express-item-price">{formatPrice(item.price * item.quantity)}</span>
                  </div>
                  <button
                    className="rm-express-item-delete"
                    onClick={() => removeFromCart(item.productId, item.sizeId)}
                    aria-label={t("Quitar", "Remove")}
                  >
                    <i className="fas fa-minus-circle"></i>
                  </button>
                </div>
              ))}
              <div className="rm-express-total">
                <span>Total</span>
                <span className="rm-express-total-price">{formatPrice(cartTotal)}</span>
              </div>
              {isMessageTooLongForLink && (
                <div className="rm-express-warning">
                  <i className="fas fa-exclamation-triangle"></i>{" "}
                  {t(
                    "Tu pedido es muy largo para enviarse automáticamente. Usa \"Copiar texto\" y pégalo en el chat que se abrirá con el restaurante.",
                    "Your order is too long to send automatically. Use \"Copy text\" and paste it into the chat that opens with the restaurant."
                  )}
                </div>
              )}
              <div className="rm-express-actions">
                {mainBranch?.whatsapp_phone && (
                  <a
                    href={getWhatsAppUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rm-whatsapp-btn"
                  >
                    <i className="fab fa-whatsapp"></i>{" "}
                    {t("Enviar por WhatsApp", "Send via WhatsApp")}
                  </a>
                )}
                <button
                  className="rm-copy-btn"
                  onClick={() => {
                    navigator.clipboard.writeText(generateWhatsAppMessage());
                    alert(t("Pedido copiado al portapapeles", "Order copied to clipboard"));
                  }}
                >
                  <i className="fas fa-copy"></i>{" "}
                  {t("Copiar texto", "Copy text")}
                </button>
              </div>
            </div>
          )}
          <div className="rm-express-bar" onClick={() => setCartExpanded(!cartExpanded)}>
            <div className="rm-express-bar-left">
              <span className="rm-cart-count">{cartItemCount}</span>
              <span className="rm-cart-label">{t("Ver pedido", "View order")}</span>
            </div>
            <div className="rm-express-bar-right">
              <span className="rm-cart-bar-total">{formatPrice(cartTotal)}</span>
              <i className={`fas fa-chevron-${cartExpanded ? "down" : "up"}`}></i>
            </div>
          </div>
        </div>
      )}

      {/* Bottom spacer when cart bar is visible */}
      {cartItemCount > 0 && <div className="rm-cart-spacer" />}
    </>
  );
};

/* ---------- Sub-components ---------- */

const ProductCard: React.FC<{
  product: Product;
  lang: string;
  onImageClick: (url: string, title: string) => void;
  onAdd: (product: Product) => void;
}> = ({ product, lang, onImageClick, onAdd }) => {
  const image = product.images?.find((img) => img.type === "cover") || product.images?.[0];
  const imageUrl = resolveImageUrl(image?.url);

  const priceLabel = product.has_size && product.sizes.length > 0
    ? `${lang === "es" ? "Desde" : "From"} ${formatPrice(Math.min(...product.sizes.map((s) => s.price)))}`
    : formatPrice(product.price);

  return (
    <div className="rm-product">
      <div className="rm-product-info">
        <h4 className="rm-product-title">{product.title}</h4>
        {product.description && (
          <p className="rm-product-desc">{product.description}</p>
        )}
        <span className="rm-product-price">{priceLabel}</span>
        {product.has_size && product.sizes.length > 0 && (
          <div className="rm-product-sizes">
            {product.sizes.map((size) => (
              <span key={size.id} className="rm-size-tag">
                {lang === "es" ? size.name : size.name_en || size.name}: {formatPrice(size.price)}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="rm-product-actions">
        {imageUrl ? (
          <div className="rm-product-img-wrapper" onClick={() => onImageClick(imageUrl, product.title)}>
            <img src={imageUrl} alt={product.title} className="rm-product-img" />
            <span className="rm-img-zoom"><i className="fas fa-search-plus"></i></span>
          </div>
        ) : (
          <div className="rm-product-img-placeholder" aria-hidden="true" />
        )}
        <button className="rm-add-btn" onClick={() => onAdd(product)} aria-label="Add">
          <i className="fas fa-plus"></i>
        </button>
      </div>
    </div>
  );
};

const HoursTable: React.FC<{ hours: Hour[]; lang: string }> = ({ hours, lang }) => {
  const weekdays = lang === "es" ? WEEKDAYS_ES : WEEKDAYS_EN;
  const grouped: Record<number, Hour[]> = {};
  hours.forEach((h) => {
    if (!grouped[h.weekday]) grouped[h.weekday] = [];
    grouped[h.weekday].push(h);
  });

  // Backend: 1=Mon through 7=Sun
  const dayOrder = [1, 2, 3, 4, 5, 6, 7];
  const today = jsToBackendWeekday();

  return (
    <table className="rm-hours-table">
      <tbody>
        {dayOrder.map((day) => {
          const dayHours = grouped[day]?.filter((h) => h.opens) || [];
          const isToday = day === today;
          return (
            <tr key={day} className={isToday ? "rm-today" : ""}>
              <td className="rm-day">{weekdays[day]}</td>
              <td className="rm-time">
                {dayHours.length === 0
                  ? lang === "es" ? "Cerrado" : "Closed"
                  : dayHours.map((h) => `${to12h(h.start_hour)} - ${to12h(h.end_hour)}`).join(", ")}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default RestaurantMenuPage;
